import type { UseFormReturn } from 'react-hook-form';
import { Typography } from '@mui/material';
import { getEntityById, getLabelFromId } from '@core/utils/collection';
import { useAppContext } from '../../../context/AppContext';
import { VestmentCategory } from '../../../enum/vestmentCategory';
import { useCustomerById } from '../../../hooks/useCustomerById';
import {
  type ChasublePanelField,
  getBodyTypeLabel,
  getChasubleFeatures,
  getChasubleMaterialField,
  getChasublePanelFields,
  getGothicTypeLabel,
  getPatchingTypeLabel,
  hasChasubleSleeveDesign,
  showsChasubleCollar,
  showsChasublePanel,
} from '../../../utils/chasuble';
import { getCollarLabel } from '../../../utils/collar';
import {
  type ChasubleItemFormValues,
  type OrderItemDialogSlotProps,
  type OrderItemFormValues,
  defaultPanelLength,
  defaultPanelWidth,
  displayMeasurement,
  formatMaterialLabel,
  formatOrderItemSizeSummary,
  getChasubleMaterial,
  getMaterialSummary,
} from '../../../utils/orderItem';
import {
  chasubleSizeFields,
  getPresetSizeForCategory,
} from '../../../utils/size';
import { OrderItemSummaryRow } from '../OrderItemDialog';

export function ChasubleItemSummary({
  methods: methodsProp,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  const methods = methodsProp as UseFormReturn<ChasubleItemFormValues>;
  const {
    vestments,
    sizes,
    fabrics,
    colors,
    collars,
    threads,
    trimmings,
    galoons,
  } = useAppContext();

  const vestmentId = methods.watch('vestment');
  const design = methods.watch('design');
  const designCode = methods.watch('designCode');
  const quantity = methods.watch('quantity');
  const sizeId = methods.watch('size');
  const forCustomerId = methods.watch('forCustomer');
  const length = methods.watch('length');
  const sleeve = methods.watch('sleeve');
  const neck = methods.watch('neck');
  const bodyFabricId = methods.watch('bodyFabric');
  const bodyColorId = methods.watch('bodyColor');
  const bodyType = methods.watch('bodyType');
  const panelFabricId = methods.watch('panelFabric');
  const panelColorId = methods.watch('panelColor');
  const centerFinish = methods.watch('centerFinish');
  const panelWidth = methods.watch('panelWidth');
  const panelLength = methods.watch('panelLength');
  const gothicType = methods.watch('gothicType');
  const panelDimension = methods.watch('panelDimension');
  const galoonId = methods.watch('galoon');
  const liningFabricId = methods.watch('liningFabric');
  const liningColorId = methods.watch('liningColor');
  const stoleSize = methods.watch('stoleSize');
  const patchingType = methods.watch('patchingType');
  const threadId = methods.watch('thread');
  const trimmingId = methods.watch('trimming');
  const collarId = methods.watch('collar');
  const collarFabricId = methods.watch('collarFabric');
  const collarColorId = methods.watch('collarColor');

  const vestment = vestments.find((v) => String(v.id) === vestmentId);
  const vestmentType = vestment?.type;
  const selectedSize = getPresetSizeForCategory(
    sizes,
    VestmentCategory.Chasuble,
    sizeId
  );
  const { data: forCustomer } = useCustomerById(forCustomerId);
  const sizeFields = chasubleSizeFields(vestmentType);
  const bodyPrefix = getChasubleFeatures(vestmentType)?.bodyType
    ? [
        getBodyTypeLabel(bodyType) ?? '-',
        ...(hasChasubleSleeveDesign(vestmentType, bodyType)
          ? [designCode || '-']
          : []),
      ].join(' / ')
    : null;
  const options = { gothicType, patchingType };
  const panelFields = showsChasublePanel(vestmentType)
    ? getChasublePanelFields(vestmentType, options)
    : [];
  const materialField = getChasubleMaterialField(vestmentType, options);
  const materialColor = getChasubleMaterial(
    vestmentType,
    { ...options, thread: threadId, trimming: trimmingId, galoon: galoonId },
    { threads, trimmings, galoons }
  );
  const material = materialColor ? formatMaterialLabel(materialColor) : null;

  const panelPart = (field: ChasublePanelField): string | null => {
    switch (field) {
      case 'fabric':
        return getLabelFromId(fabrics, panelFabricId) ?? '-';
      case 'color':
        return getLabelFromId(colors, panelColorId) ?? '-';
      case 'width':
        return `W ${displayMeasurement(
          panelWidth,
          defaultPanelWidth(centerFinish, design?.centerFinish)
        )}`;
      case 'length':
        return `L ${displayMeasurement(
          panelLength,
          defaultPanelLength({
            customer: forCustomer,
            size: selectedSize,
            vestmentType,
            vestmentCategory: VestmentCategory.Chasuble,
            lineItemLength: length,
          })
        )}`;
      case 'gothicType':
        return getGothicTypeLabel(gothicType) ?? '-';
      case 'galoon':
        return getMaterialSummary(galoons, galoonId) ?? '-';
      case 'dimension':
        return panelDimension || '-';
      case 'stoleSize':
        return `Stole ${stoleSize || '-'}`;
      default:
        return null;
    }
  };
  const panelSummary = panelFields
    .map(panelPart)
    .filter((part) => part !== null)
    .join(' / ');

  return (
    <>
      <OrderItemSummaryRow label="Quantity" fadeKey={quantity}>
        <Typography variant="caption" color="text.secondary">
          {quantity ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      {sizeFields.length ? (
        <OrderItemSummaryRow
          label="Size"
          fadeKey={`${vestmentType}-${sizeId}-${forCustomerId}-${length}-${sleeve}-${neck}`}
        >
          <Typography variant="caption" color="text.secondary">
            {formatOrderItemSizeSummary(sizeFields, {
              sizeName: getLabelFromId(sizes, sizeId),
              size: selectedSize,
              customer: forCustomer,
              vestmentType,
              vestmentCategory: VestmentCategory.Chasuble,
              length,
              sleeve,
              neck,
            })}
          </Typography>
        </OrderItemSummaryRow>
      ) : null}
      <OrderItemSummaryRow
        label="Body"
        fadeKey={`${bodyType}-${designCode}-${bodyFabricId}-${bodyColorId}`}
        colorHex={getEntityById(colors, bodyColorId)?.hexCode}
      >
        <Typography variant="caption" color="text.secondary">
          {bodyPrefix ? `${bodyPrefix} / ` : null}
          {getLabelFromId(fabrics, bodyFabricId) ?? '-'} /{' '}
          {getLabelFromId(colors, bodyColorId) ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      {panelSummary ? (
        <OrderItemSummaryRow
          label="Panel"
          fadeKey={`${gothicType}-${galoonId}-${panelFabricId}-${panelColorId}-${panelDimension}-${panelWidth}-${centerFinish}-${design?.centerFinish}-${panelLength}-${stoleSize}-${length}-${forCustomerId}-${sizeId}`}
          colorHex={getEntityById(colors, panelColorId)?.hexCode}
        >
          <Typography variant="caption" color="text.secondary">
            {panelSummary}
          </Typography>
        </OrderItemSummaryRow>
      ) : null}
      {panelFields.includes('patching') ? (
        <OrderItemSummaryRow
          label="Patching"
          fadeKey={`${patchingType}-${threadId}-${trimmingId}`}
          colorHex={materialColor?.hexCode}
        >
          <Typography variant="caption" color="text.secondary">
            {getPatchingTypeLabel(patchingType) ?? '-'}
            {materialField ? ` / ${material ?? '-'}` : null}
          </Typography>
        </OrderItemSummaryRow>
      ) : null}
      {panelFields.includes('trimming') ? (
        <OrderItemSummaryRow
          label="Trimming"
          fadeKey={trimmingId}
          colorHex={materialColor?.hexCode}
        >
          <Typography variant="caption" color="text.secondary">
            {material ?? '-'}
          </Typography>
        </OrderItemSummaryRow>
      ) : null}
      {panelFields.includes('liningFabric') ? (
        <OrderItemSummaryRow
          label="Lining"
          fadeKey={`${liningFabricId}-${liningColorId}`}
          colorHex={getEntityById(colors, liningColorId)?.hexCode}
        >
          <Typography variant="caption" color="text.secondary">
            {getLabelFromId(fabrics, liningFabricId) ?? '-'} /{' '}
            {getLabelFromId(colors, liningColorId) ?? '-'}
          </Typography>
        </OrderItemSummaryRow>
      ) : null}
      {showsChasubleCollar(vestmentType) ? (
        <OrderItemSummaryRow
          label="Collar"
          fadeKey={`${collarId}-${collarFabricId}-${collarColorId}`}
          colorHex={getEntityById(colors, collarColorId)?.hexCode}
        >
          <Typography variant="caption" color="text.secondary">
            {getCollarLabel(vestment, collars, collarId) ?? '-'} /{' '}
            {getLabelFromId(fabrics, collarFabricId) ?? '-'} /{' '}
            {getLabelFromId(colors, collarColorId) ?? '-'}
          </Typography>
        </OrderItemSummaryRow>
      ) : null}
    </>
  );
}
